// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func.apply(this, args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

function initializeFormDrawer(config) {
  const { formId, panelId, isMobile } = config;
  const suffix = isMobile ? '--mobile' : '';

  const openFormBtn = document.querySelector(`#inquiry__form-button${isMobile ? '-mobile' : ''}--open`);
  const formPanel = document.getElementById(panelId);
  const backBtn = document.getElementById(`inquiry-form-back${suffix}`);
  const closeFormBtn = document.getElementById(`inquiry-form-close${suffix}`);
  const form = document.getElementById(formId);

  // Debounce delay in milliseconds
  const DEBOUNCE_DELAY = 300;

  // Validation patterns
  const patterns = {
    email: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
    name: /^[a-zA-Z\s'-]+$/,
    phone: /^\+?[0-9]{6,15}$/ // Only numbers with optional + prefix, 6-15 digits
  };

  // Error messages
  const errorMessages = {
    required: 'This field is required',
    email: 'Please enter a valid email address',
    name: 'Please enter a valid name (letters only)',
    phone: 'Please enter a valid phone number'
  };

  // Initialize textarea character count
  initTextareaCharacterCount();

  // Initialize real-time validation
  initRealTimeValidation();

  // Open form panel
  if (openFormBtn) {
    openFormBtn.addEventListener('click', function () {
      // Auto-fill product code / name / variant from the current PDP. fillInquiryFields
      // fills both desktop and --mobile field sets, and is a no-op off product pages
      // (it's only defined by atc-nd-enquiry on a product). Without this, reaching the
      // form via Contact Us → Make Inquiry leaves the product fields blank, unlike the
      // PDP Enquire button which already fills them.
      if (typeof window.fillInquiryFields === 'function') window.fillInquiryFields();
      formPanel.classList.remove('hidden');
      setTimeout(() => {
        formPanel.classList.add('active');
      }, 10);
    });
  }

  // Back button
  if (backBtn) {
    backBtn.addEventListener('click', resetState);
  }

  // Close form button
  if (closeFormBtn) {
    closeFormBtn.addEventListener('click', function () {
      resetState();
      if (isMobile == '' && typeof window.toggleContactDrawer === 'function') {
        window.toggleContactDrawer(false);
      } else {
        document.querySelector('mobile-menu').toggleDrawer(false);
      }
    });
  }

  function resetState() {
    formPanel.classList.remove('active');
    setTimeout(() => {
      formPanel.classList.add('hidden');
    }, 300);
  }

  window.resetFormState = resetState;

  // Initialize textarea character count functionality
  function initTextareaCharacterCount() {
    const textarea = document.getElementById(`inquiry-message${suffix}`);
    if (!textarea) return;

    const formField = textarea.closest('.form-field');
    const characterLimitContainer = formField?.querySelector('.textarea__character-limit');
    const countSpan = characterLimitContainer?.querySelector('.update-counts');
    const maxLength = parseInt(textarea.getAttribute('data-maxlength') || 300);

    if (!countSpan) return;

    let wasExceeding = false;

    // Trigger shake animation
    function triggerShake() {
      textarea.classList.add('shake');
      characterLimitContainer.classList.add('shake');

      // Remove shake class after animation completes
      setTimeout(() => {
        textarea.classList.remove('shake');
        characterLimitContainer.classList.remove('shake');
      }, 400);
    }

    // Update count on input
    textarea.addEventListener('input', function () {
      const currentLength = this.value.length;
      countSpan.textContent = currentLength;

      // Check if exceeded limit
      if (currentLength > maxLength) {
        textarea.classList.add('has-error');
        characterLimitContainer.classList.add('has-error');

        // Trigger shake only when first exceeding the limit
        if (!wasExceeding) {
          triggerShake();
          wasExceeding = true;
        }
      } else {
        textarea.classList.remove('has-error');
        characterLimitContainer.classList.remove('has-error');
        wasExceeding = false;
      }
    });

    // Initialize count on page load
    countSpan.textContent = textarea.value.length;
  }

  // Initialize real-time validation for inputs
  function initRealTimeValidation() {
    // Name field validation
    const nameInput = document.getElementById(`inquiry-name${suffix}`);
    if (nameInput) {
      // Restrict input to only letters, spaces, hyphens, and apostrophes (no numbers)
      nameInput.addEventListener('keypress', function (e) {
        const allowedChars = /[a-zA-Z\s'-]/;
        if (!allowedChars.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
          e.preventDefault();
        }
      });

      // Handle paste - remove numbers and special characters
      nameInput.addEventListener('paste', function (e) {
        e.preventDefault();
        const pastedText = (e.clipboardData || window.clipboardData).getData('text');
        const cleanedText = pastedText.replace(/[^a-zA-Z\s'-]/g, '');
        const cursorPos = this.selectionStart;
        const textBefore = this.value.substring(0, cursorPos);
        const textAfter = this.value.substring(this.selectionEnd);
        this.value = textBefore + cleanedText + textAfter;
        this.setSelectionRange(cursorPos + cleanedText.length, cursorPos + cleanedText.length);
      });

      nameInput.addEventListener('blur', function () {
        validateField(this, 'name');
      });

      // Debounced validation on input - validates while typing after delay
      const debouncedNameValidation = debounce(function () {
        validateField(nameInput, 'name');
      }, DEBOUNCE_DELAY);

      nameInput.addEventListener('input', function () {
        // Filter out any numbers or invalid characters on input (for autofill, etc.)
        const cleanedValue = this.value.replace(/[^a-zA-Z\s'-]/g, '');
        if (this.value !== cleanedValue) {
          this.value = cleanedValue;
        }
        clearFieldError(this);
        // Only validate if field has error class or has content
        if (this.classList.contains('has-error') || this.value.trim()) {
          debouncedNameValidation();
        }
      });
    }

    // Email field validation
    const emailInput = document.getElementById(`inquiry-email${suffix}`);
    if (emailInput) {
      emailInput.addEventListener('blur', function () {
        validateField(this, 'email');
      });
      // Debounced validation on input
      const debouncedEmailValidation = debounce(function () {
        validateField(emailInput, 'email');
      }, DEBOUNCE_DELAY);
      emailInput.addEventListener('input', function () {
        clearFieldError(this);
        if (this.classList.contains('has-error') || this.value.trim()) {
          debouncedEmailValidation();
        }
      });
    }

    // Phone field validation (optional but validate format if filled)
    const phoneInput = document.getElementById(`inquiry-phone${suffix}`);
    if (phoneInput) {
      // Restrict input to only numbers (and + for international)
      phoneInput.addEventListener('keypress', function (e) {
        const allowedChars = /[0-9+]/;
        if (!allowedChars.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
          e.preventDefault();
        }
      });

      // Also handle paste - remove non-numeric characters
      phoneInput.addEventListener('paste', function (e) {
        e.preventDefault();
        const pastedText = (e.clipboardData || window.clipboardData).getData('text');
        const cleanedText = pastedText.replace(/[^0-9+]/g, '');
        const cursorPos = this.selectionStart;
        const textBefore = this.value.substring(0, cursorPos);
        const textAfter = this.value.substring(this.selectionEnd);
        this.value = textBefore + cleanedText + textAfter;
        this.setSelectionRange(cursorPos + cleanedText.length, cursorPos + cleanedText.length);
      });

      // Filter out any non-numeric on input (for autofill, etc.)
      phoneInput.addEventListener('input', function () {
        const cleanedValue = this.value.replace(/[^0-9+]/g, '');
        if (this.value !== cleanedValue) {
          this.value = cleanedValue;
        }
        clearFieldError(this);
      });

      phoneInput.addEventListener('blur', function () {
        validateField(this, 'phone');
      });
    }

    // Message textarea validation
    const messageTextarea = document.getElementById(`inquiry-message${suffix}`);
    if (messageTextarea) {
      messageTextarea.addEventListener('blur', function () {
        validateTextarea(this);
      });
      // Debounced validation for textarea (separate from character count which is immediate)
      const debouncedTextareaValidation = debounce(function () {
        validateTextarea(messageTextarea);
      }, DEBOUNCE_DELAY);
      messageTextarea.addEventListener('input', function () {
        // Character count is handled separately (immediate, not debounced)
        // Only debounce the error message validation
        if (this.classList.contains('has-error')) {
          debouncedTextareaValidation();
        }
      });
    }
  }

  // Validate a single field
  function validateField(element, type) {
    const value = element.value.trim();
    const isRequired = element.hasAttribute('required');
    const errorEl = element.parentElement.querySelector('.form-field-error');

    // Clear previous error
    element.classList.remove('has-error');
    if (errorEl) errorEl.textContent = '';

    // Check required
    if (isRequired && !value) {
      element.classList.add('has-error');
      if (errorEl) errorEl.textContent = errorMessages.required;
      return false;
    }

    // Skip pattern validation if field is empty and not required
    if (!value) return true;

    // Pattern validation
    if (type === 'email' && !patterns.email.test(value)) {
      element.classList.add('has-error');
      if (errorEl) errorEl.textContent = errorMessages.email;
      return false;
    }

    if (type === 'name' && !patterns.name.test(value)) {
      element.classList.add('has-error');
      if (errorEl) errorEl.textContent = errorMessages.name;
      return false;
    }

    if (type === 'phone' && !patterns.phone.test(value)) {
      element.classList.add('has-error');
      if (errorEl) errorEl.textContent = errorMessages.phone;
      return false;
    }

    return true;
  }

  // Validate textarea
  function validateTextarea(element) {
    const value = element.value.trim();
    const isRequired = element.hasAttribute('required');
    const errorEl = element.parentElement.querySelector('.form-field-error');
    const maxLength = parseInt(element.getAttribute('data-maxlength') || element.getAttribute('maxlength') || 300);

    // Don't clear character limit error classes, only the required error message
    if (errorEl) errorEl.textContent = '';

    // Check required
    if (isRequired && !value) {
      element.classList.add('has-error');
      if (errorEl) errorEl.textContent = errorMessages.required;
      return false;
    }

    // Check if exceeds max length (no message, just red border handled by input event)
    if (value.length > maxLength) {
      return false;
    }

    // If no errors and not exceeding limit, remove error class
    if (value.length <= maxLength) {
      element.classList.remove('has-error');
    }

    return true;
  }

  // Clear field error on input
  function clearFieldError(element) {
    element.classList.remove('has-error');
    const errorEl = element.parentElement.querySelector('.form-field-error');
    if (errorEl) errorEl.textContent = '';
  }

  // Validate radio group
  function validateRadioGroup(name) {
    const radioGroup = form.querySelector(`input[name="${name}"]`);
    if (!radioGroup) return true;

    const isRequired = radioGroup.hasAttribute('required');
    const isChecked = form.querySelector(`input[name="${name}"]:checked`);
    const formField = radioGroup.closest('.form-field');
    const radioGroupContainer = formField?.querySelector('.form-radio-group');
    const errorEl = formField?.querySelector('.form-field-error');

    // Clear previous error
    if (radioGroupContainer) radioGroupContainer.classList.remove('has-error');
    if (errorEl) errorEl.textContent = '';

    if (isRequired && !isChecked) {
      if (radioGroupContainer) radioGroupContainer.classList.add('has-error');
      if (errorEl) errorEl.textContent = errorMessages.required;
      return false;
    }

    return true;
  }

  // Form validation and submission
  if (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();

      // Reset all errors
      form.querySelectorAll('.form-field-error').forEach((el) => (el.textContent = ''));
      form.querySelectorAll('.has-error').forEach((el) => el.classList.remove('has-error'));
      const errorContainer = document.getElementById('form-errors');
      if (errorContainer) errorContainer.classList.add('hidden');

      // Validate all fields
      let isValid = true;

      // Name validation
      const nameInput = document.getElementById(`inquiry-name${suffix}`);
      if (nameInput && !validateField(nameInput, 'name')) {
        isValid = false;
      }

      // Email validation
      const emailInput = document.getElementById(`inquiry-email${suffix}`);
      if (emailInput && !validateField(emailInput, 'email')) {
        isValid = false;
      }

      // Phone validation
      const phoneInput = document.getElementById(`inquiry-phone${suffix}`);
      if (phoneInput && !validateField(phoneInput, 'phone')) {
        isValid = false;
      }

      // Message validation
      const messageTextarea = document.getElementById(`inquiry-message${suffix}`);
      if (messageTextarea) {
        const value = messageTextarea.value.trim();
        const maxLength = parseInt(messageTextarea.getAttribute('data-maxlength') || messageTextarea.getAttribute('maxlength') || 300);
        const isRequired = messageTextarea.hasAttribute('required');
        const errorEl = messageTextarea.parentElement.querySelector('.form-field-error');
        const formField = messageTextarea.closest('.form-field');
        const characterLimitContainer = formField?.querySelector('.textarea__character-limit');

        if (isRequired && !value) {
          messageTextarea.classList.add('has-error');
          if (errorEl) errorEl.textContent = errorMessages.required;
          isValid = false;
        } else if (value.length > maxLength) {
          messageTextarea.classList.add('has-error');
          if (characterLimitContainer) characterLimitContainer.classList.add('has-error');
          isValid = false;
        }
      }

      // Radio group validation
      if (!validateRadioGroup('contact[preference]')) {
        isValid = false;
      }

      if (isValid) {
        // Show loading
        const submitBtn = form.querySelector('.form-submit-button');
        if (submitBtn) {
          const textSpan = submitBtn.querySelector('span:first-child');
          const loadingSpan = submitBtn.querySelector('.form-submit-loading');
          if (textSpan) textSpan.classList.add('hidden');
          if (loadingSpan) loadingSpan.classList.remove('hidden');
        }

        // Submit form
        setTimeout(() => {
          form.submit();
        }, 500);
      } else {
        // Scroll to first error
        const firstError = form.querySelector('.has-error');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstError.focus();
        }
      }
    });
  }
}

document.addEventListener('DOMContentLoaded', function () {
  // Initialize desktop form
  if (document.getElementById('inquiry-form')) {
    initializeFormDrawer({
      formId: 'inquiry-form',
      panelId: 'inquiry-form-panel',
      isMobile: false,
    });
  }

  // Initialize mobile form
  if (document.getElementById('inquiry-form--mobile')) {
    initializeFormDrawer({
      formId: 'inquiry-form--mobile',
      panelId: 'inquiry-form-panel--mobile',
      isMobile: true,
    });
  }
});